import { useQuery } from "@tanstack/react-query";
import {
  deleteNewsComment,
  editNewsComment,
  GET_NEWS_COMMENTS_QUERY_KEY,
  getComments,
  postComment,
  toggleCommentReaction,
} from "./api/newsScreenApi";
import { Flex, Spinner, Text } from "@chakra-ui/react";
import { Comment } from "../../components/comment/Comment";
import { SubmitForm } from "../../components/comment/SubmitForm";
import { t } from "i18next";
import { BlogCommentEntity } from "../../types";
import { useRef } from "react";
import { useSendComment } from "../../hooks/comments/useSendComment";
import { useUserData } from "../../hooks/useUserData";
import { FunnelContextArea } from "../../components/funnel/types";
import { setFunnelModalConfig } from "../../components/funnel/FunnelModal";
import { FUNNEL_TYPES } from "../../components/funnel/utils/contants";
import { useDeleteComment } from "../../hooks/comments/useDeleteComment";
import { useEditComment } from "../../hooks/comments/useEditComment";
import { useCommentReactions } from "../../hooks/comments/useCommentReactions";
import { CommentsDisabled } from "../../components/comment/CommentsDisabled";
import { CommentsDisabledLarge } from "../../components/comment/CommentsDisabledLarge";

interface NewsDetailsCommentsProps {
  articleId: number;
  areCommentsDisabled?: boolean;
}
export function NewsDetailsComments({
  articleId,
  areCommentsDisabled,
}: NewsDetailsCommentsProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const queryKey = [...GET_NEWS_COMMENTS_QUERY_KEY, articleId];
  const sendComment = useSendComment(queryKey, (data) =>
    postComment({ articleId, ...data }),
  );
  const deleteComment = useDeleteComment(queryKey, ({ articleId, commentId }) =>
    deleteNewsComment({ articleId, commentId }),
  );
  const editComment = useEditComment(queryKey, ({ body, comment }) =>
    editNewsComment({
      articleId: comment.object_id,
      commentId: comment.id,
      body,
    }),
  );
  const reactToComment = useCommentReactions(
    queryKey,
    ({ reactionTypeId, comment }) => {
      return toggleCommentReaction(
        comment.object_id,
        comment.id,
        reactionTypeId,
      );
    },
  );
  const { userData } = useUserData();

  const { isLoading, data } = useQuery({
    queryKey,
    queryFn: () => getComments({ articleId }),
    refetchOnWindowFocus: false,
  });

  const handleSubmit = (message: string) => {
    if (!userData) {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context: {
          modalContext: FunnelContextArea.newsComments,
        },
        options: {
          onSuccess: () => {
            if (message.trim()) {
              sendComment({
                body: message,
              });
            }
          },
        },
      });
    } else if (message.trim()) {
      sendComment({
        body: message,
      });
    }
  };

  if (isLoading) {
    return (
      <Flex width="full" alignItems="center" justifyContent="center">
        <Spinner color="blaze.blaze" />
      </Flex>
    );
  }

  const comments = data?.data.payload.results;
  let commentCount = 0;
  comments?.forEach((comment) => {
    commentCount += 1;
    if (comment.replies?.length) {
      commentCount += comment.replies.length;
    }
  });
  return (
    <Flex flexDirection="column" gap="16px">
      <Text
        color="dune.dune"
        fontSize="24px"
        fontWeight="500"
        letterSpacing=".5px"
      >
        {t("screens.hub.commentCount", { count: commentCount })}
      </Text>
      {areCommentsDisabled && commentCount > 0 && (
        <Flex
          height="56px"
          alignItems="center"
          justifyContent="center"
          bg="transparent.white.05"
          borderRadius="24px"
        >
          <CommentsDisabled />
        </Flex>
      )}
      {!areCommentsDisabled && (
        <SubmitForm
          formRef={formRef}
          handleSubmit={handleSubmit}
          placeholder={t("screens.hub.placeholder")}
          isSmall
        />
      )}
      <Flex flexDirection="column-reverse" gap="16px">
        {areCommentsDisabled && commentCount === 0 && <CommentsDisabledLarge showBackground />}
        {comments?.map((comment) => {
          return (
            <Comment
              key={comment.id}
              comment={comment}
              contentEditable
              submitReply={(body, parentComment) =>
                sendComment({
                  body,
                  parentComment: parentComment as BlogCommentEntity,
                })
              }
              reactToComment={(comment, reactionTypeId) =>
                reactToComment({ comment, reactionTypeId })
              }
              deleteComment={deleteComment}
              editComment={(comment, body) => editComment({ comment, body })}
              commentsDisabled={areCommentsDisabled}
            />
          );
        })}
      </Flex>
    </Flex>
  );
}
